import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CallBackProps, Placement, Props, Step } from 'react-joyride';
import { STATUS } from 'react-joyride';

import { useResponsive } from 'contexts/ResponsiveContext';
import type { NoopT } from 'declarations/types/common';

type OnboardingTourContextT = {
  isRunningTour: boolean;
  steps: Step[];
  handleStartTourClick: NoopT;
  handleJoyrideCallback: Props['callback'];
};

type OnboardingTourProviderPropsT = {
  children: ReactNode;
};

const contextDefaultValue = {
  isRunningTour: false,
  steps: [],
  handleJoyrideCallback: (): undefined => undefined,
  handleStartTourClick: (): undefined => undefined,
};

const OnboardingTourContext = createContext<OnboardingTourContextT>(contextDefaultValue);

const OnboardingTourProvider: FC<OnboardingTourProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const [isRunningTour, setIsRunningTour] = useState(false);

  /**
   * Variables
   */
  const steps = useMemo(
    () => [
      ...(isMobile
        ? [
            {
              target: '.collapse-report-btn',
              content: 'Click to open the report section',
              disableBeacon: true,
              spotlightClicks: true,
              spotlightPadding: 6,
              offset: 4,
            },
          ]
        : []),
      {
        target: '.user-card',
        content: 'Click on user avatar to open menu',
        disableBeacon: true,
        spotlightClicks: false,
        spotlightPadding: 0,
        offset: 4,
      },
      {
        target: '.open-ticket-btn',
        content: 'Click button to open a new ticket',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '#searchTerm',
        content: 'Try to search tickets by RO Number',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.filters',
        content: 'Try to filter tickets',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.sorting',
        content: 'Click to sort by dealership',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.ticket-row-1',
        content: 'Click on row to open ticket details',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 400,
        offset: 4,
      },
      {
        target: '.select-ticket',
        content: 'Click to select the ticket (to edit, share, reassign, or mark as read/unread)',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 400,
        offset: 140,
      },
      {
        target: '.mark-read',
        content: 'Click to mark selected ticket/s as read/unread',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 0,
        offset: 14,
      },
      {
        target: '.share-ticket',
        content: 'Click to share selected ticket with someone',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.schedule-tab',
        content: 'Review schedule tickets and actions',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.cards-report-view',
        content: 'Review dealer schedule filters',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },

      {
        target: '.roof-tops-list',
        content: 'Try to filter tickets by Rooftop',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 0,
        offset: 4,
        placement: 'right-end' as Placement,
      },
    ],
    [isMobile]
  );

  /**
   * Handlers
   */
  const handleStartTourClick = useCallback(() => {
    setIsRunningTour(true);
  }, []);
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setIsRunningTour(false);
    }
  }, []);

  return (
    <OnboardingTourContext.Provider
      value={{ isRunningTour, steps, handleStartTourClick, handleJoyrideCallback }}
    >
      {children}
    </OnboardingTourContext.Provider>
  );
};

const useOnboardingTour = (): OnboardingTourContextT => useContext(OnboardingTourContext);

export { OnboardingTourProvider, useOnboardingTour };
